import React, { useState } from "react";
import { PinInput } from "./ui/PinInput";
import { NumericKeypad } from "./ui/NumericKeypad";
import { User } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { useAppData } from '../hooks/useAppData';
import { getAuth, signInWithEmailAndPassword } from "firebase/auth";

interface LoginScreenProps {
  userType: 'comprador' | 'vendedor' | 'socio' | 'admin' | 'contabilidad';
  onLoginSuccess: (userData: { userType: string; userInput: string; pin?: string; password?: string; userId?: string }) => void;
}

const USER_CONFIG = {
  comprador: {
    label: "Comprador",
    desc: "Propietario de lotes",
    color: "blue",
    colorHex: "#2563eb",
    inputLabel: "Número de Lote",
    inputPlaceholder: "Ej: 123 o 123,124",
    inputHelp: "Puedes ingresar uno o varios lotes separados por comas"
  },
  vendedor: {
    label: "Vendedor",
    desc: "Agente de ventas",
    color: "green",
    colorHex: "#22c55e",
    inputLabel: "Nombre de usuario",
    inputPlaceholder: "Ej: Juan Pérez",
    inputHelp: ""
  },
  socio: {
    label: "Socio",
    desc: "Socio del proyecto",
    color: "purple",
    colorHex: "#a21caf",
    inputLabel: "Nombre de usuario",
    inputPlaceholder: "Ej: Socio Principal",
    inputHelp: ""
  },
  admin: {
    label: "Administrador",
    desc: "Superusuario del sistema",
    color: "gray",
    colorHex: "#6b7280",
    inputLabel: "Usuario",
    inputPlaceholder: "admin",
    inputHelp: ""
  },
  contabilidad: {
    label: "Contabilidad",
    desc: "Usuario de contabilidad",
    color: "sky",
    colorHex: "#0ea5e9",
    inputLabel: "Usuario",
    inputPlaceholder: "contabilidad",
    inputHelp: ""
  }
};

function getBgColor(type: string, selected: boolean) {
  if (!selected) return "bg-white";
  const config = USER_CONFIG[type as keyof typeof USER_CONFIG];
  return `bg-${config.color}-50`;
}

function getBorderColor(type: string, selected: boolean) {
  if (!selected) return "border-gray-200";
  const config = USER_CONFIG[type as keyof typeof USER_CONFIG];
  return `border-${config.color}-500`;
}

function getIconColor(type: string, selected: boolean) {
  if (!selected) return "#a3a3a3";
  const config = USER_CONFIG[type as keyof typeof USER_CONFIG];
  return config.colorHex;
}

export default function LoginScreen({ userType, onLoginSuccess }: LoginScreenProps) {
  const [step, setStep] = useState(1);
  const [userInput, setUserInput] = useState("");
  const [pin, setPin] = useState("");
  const [password, setPassword] = useState(""); // Para admin
  const [error, setError] = useState("");
  const { sellers, usuarios } = useAppData();
  const config = USER_CONFIG[userType] || { label: 'Contabilidad', desc: 'Usuario de contabilidad', color: 'sky', colorHex: '#0ea5e9', inputLabel: 'Usuario', inputPlaceholder: 'contabilidad', inputHelp: '' };

  // Validación para habilitar el botón continuar
  const isContinueEnabled = userInput.trim().length > 0;

  // Resetear PIN/contraseña y error al volver al paso 1
  const handleBack = () => {
    setStep(1);
    setPin("");
    setPassword("");
    setError("");
  };

  // Avanzar al paso 2
  const handleContinue = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isContinueEnabled) return;
    if (userType === 'comprador') {
      // Validar que el número de lote existe en algún usuario comprador
      const lotesInput = userInput.split(',').map((l: string) => l.trim());
      const match = usuarios.some((u: any) =>
        u.tipo === 'comprador' &&
        u.activo &&
        !u.bloqueado &&
        u.lotesAsignados?.some((lote: string) => lotesInput.includes(lote))
      );
      if (!match) {
        setError('El número de lote ingresado no coincide con ningún lote registrado. Por favor verifica que escribiste correctamente el número de lote.');
        return;
      }
    }
    if (userType === 'vendedor') {
      const inputName = userInput.trim().toLowerCase();
      const match = sellers.some((seller: { name: string }) => {
        const firstName = seller.name.split(' ')[0].toLowerCase();
        return inputName === firstName;
      });
      if (!match) {
        setError('El nombre ingresado no coincide con ningún vendedor registrado. Por favor verifica que escribiste correctamente el primer nombre y vuelve a intentarlo.');
        return;
      }
    }
    if (userType === 'admin') {
      // Validar que el nombre coincida con un usuario tipo admin
      const inputName = userInput.trim();
      const match = usuarios.some((u: any) => u.tipo === 'admin' && u.nombre === inputName);
      if (!match) {
        setError('El nombre ingresado no coincide con ningún administrador registrado.');
        return;
      }
    }
    if (userType === 'socio') {
      const inputName = userInput.trim();
      const match = usuarios.some((u: any) => u.tipo === 'socio' && u.nombre === inputName);
      if (!match) {
        setError('El nombre ingresado no coincide con ningún socio registrado.');
        return;
      }
    }
    setStep(2);
    setPin("");
    setPassword("");
    setError("");
  };

  // Login cuando el PIN/contraseña está completo
  const handleLogin = async () => {
    let firebaseError: any = null;
    if (userType === 'admin' || userType === 'socio' || userType === 'contabilidad') {
      // 1. Intentar login con Firebase Auth (Prioridad para Admin)
      if (userType === 'admin') {
        const auth = getAuth();
        // Asumimos que el usuario ingresó el email completo o lo construimos
        const email = userInput.includes('@') ? userInput : `${userInput}@bimobi.app`;

        try {
          const userCredential = await signInWithEmailAndPassword(auth, email, password);
          // Si tiene éxito, pasamos.
          onLoginSuccess({ userType, userInput, password, userId: userCredential.user.uid });
          return;
        } catch (error: any) {
          console.log("Firebase Auth failed, falling back to legacy check", error);
          firebaseError = error;
          // Si falla Firebase Auth, seguimos con el chequeo legacy de abajo
        }
      }

      // 2. Legacy Check (Base de datos local/Firestore)
      const inputNombre = userInput.trim().toLowerCase();
      const usuario = usuarios.find((u: any) =>
        u.tipo === userType &&
        u.nombre.trim().toLowerCase() === inputNombre &&
        u.password === password &&
        u.activo &&
        !u.bloqueado
      );

      if (!usuario) {
        // Si falló Firebase y también Legacy, mostrar el error de Firebase si existe
        if (firebaseError) {
          let msg = 'Error de autenticación.';
          if (firebaseError.code === 'auth/user-not-found' || firebaseError.code === 'auth/invalid-credential') msg = 'Usuario no encontrado. ¿Ya creaste la cuenta en Firebase?';
          if (firebaseError.code === 'auth/wrong-password') msg = 'Contraseña incorrecta.';
          if (firebaseError.code === 'auth/too-many-requests') msg = 'Demasiados intentos fallidos. Intenta más tarde.';
          setError(msg);
        } else {
          setError('Contraseña incorrecta o usuario no encontrado.');
        }
        setPassword("");
        return;
      }
      onLoginSuccess({ userType, userInput, password, userId: usuario.id });
      return;
    }
    if (userType === 'comprador') {
      // Buscar usuario comprador por número de lote y PIN
      const lotesInput = userInput.split(',').map((l: string) => l.trim());
      const usuario = usuarios.find((u: any) =>
        u.tipo === 'comprador' &&
        u.pin === pin &&
        u.activo &&
        !u.bloqueado &&
        u.lotesAsignados?.some((lote: string) => lotesInput.includes(lote))
      );
      if (!usuario) {
        setError('PIN incorrecto. Intenta de nuevo.');
        setPin("");
        return;
      }
      onLoginSuccess({ userType, userInput, pin, userId: usuario.id });
      return;
    }
    if (userType === 'vendedor') {
      const inputName = userInput.trim().toLowerCase();
      const vendedor = sellers.find((seller: any) => seller.name && seller.name.split(' ')[0].toLowerCase() === inputName);
      if (!vendedor) {
        setError('No se encontró el vendedor.');
        return;
      }
      const usuarioVendedor = usuarios.find((u: any) => u.tipo === 'vendedor' && u.nombre === vendedor.name && u.pin === pin && u.activo && !u.bloqueado);
      if (!usuarioVendedor) {
        setError('PIN incorrecto. Intenta de nuevo.');
        setPin("");
        return;
      }
      onLoginSuccess({ userType, userInput, pin, userId: vendedor.id });
      return;
    }
    onLoginSuccess({ userType, userInput, pin });
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="w-full max-w-[400px] bg-white rounded-xl border border-gray-200 shadow-sm p-8 flex flex-col items-center">
        {/* Logo/título */}
        <div className="flex flex-col items-center mb-8">
          <h1 className="text-xl font-semibold text-gray-900 mb-1 tracking-tight">Bimobi</h1>
          <p className="text-gray-500 text-center text-sm">Acceso de {config.label}</p>
        </div>
        {/* Paso 1: Datos del usuario (admin, socio y contabilidad: usuario + contraseña) */}
        {step === 1 && (userType === 'admin' || userType === 'socio' || userType === 'contabilidad') && (
          <form onSubmit={e => { e.preventDefault(); handleLogin(); }} className="w-full flex flex-col items-center">
            <div className="w-full mb-4">
              <label className="block mb-1.5 text-sm font-medium text-gray-700">Usuario</label>
              <input
                type="text"
                value={userInput}
                onChange={e => setUserInput(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-300 focus:ring-2 focus:ring-gray-100 transition-all duration-200"
                placeholder={config.inputPlaceholder}
                autoComplete={`${userType}-username`}
              />
            </div>
            <div className="w-full mb-6">
              <label className="block mb-1.5 text-sm font-medium text-gray-700">Contraseña</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-300 focus:ring-2 focus:ring-gray-100 transition-all duration-200"
                placeholder={userType === 'admin' ? 'Contraseña del administrador' : 'Contraseña del socio'}
                autoComplete={`${userType}-password`}
              />
            </div>
            {error && <div className="w-full mb-4 text-center text-red-600 text-sm bg-red-50 py-2 rounded-md border border-red-100">{error}</div>}
            <button
              type="submit"
              className={`w-full py-2.5 rounded-lg font-medium text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2
                ${(userInput.trim().length > 0 && password.length > 0)
                  ? 'bg-gray-900 text-white hover:bg-black shadow-sm'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
              disabled={userInput.trim().length === 0 || password.length === 0}
            >
              Ingresar
            </button>
          </form>
        )}
        {/* Paso 1: Otros tipos de usuario */}
        {step === 1 && userType !== 'admin' && userType !== 'socio' && userType !== 'contabilidad' && (
          <form onSubmit={handleContinue} className="w-full flex flex-col items-center">
            <div className="w-full mb-6">
              <label className="block mb-1.5 text-sm font-medium text-gray-700">
                {config.inputLabel}
              </label>
              <input
                type="text"
                value={userInput}
                onChange={e => setUserInput(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-300 focus:ring-2 focus:ring-gray-100 transition-all duration-200"
                placeholder={config.inputPlaceholder}
                autoComplete="username"
              />
              {config.inputHelp && <p className="text-xs text-gray-500 mt-1.5">{config.inputHelp}</p>}
            </div>
            {error && <div className="w-full mb-4 text-center text-red-600 text-sm bg-red-50 py-2 rounded-md border border-red-100">{error}</div>}
            <button
              type="submit"
              className={`w-full py-2.5 rounded-lg font-medium text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2
                ${isContinueEnabled
                  ? 'bg-gray-900 text-white hover:bg-black shadow-sm'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
              disabled={!isContinueEnabled}
            >
              Siguiente
            </button>
          </form>
        )}
        {/* Paso 2: PIN para comprador y vendedor */}
        {step === 2 && userType !== 'admin' && userType !== 'socio' && (
          <div className="w-full flex flex-col items-center">
            <div className="w-full mb-6 flex justify-between items-center">
              <span className="block text-sm font-medium text-gray-700">Ingrese su PIN</span>
              <button
                className="text-gray-500 hover:text-gray-900 text-sm font-medium transition-colors"
                onClick={handleBack}
                type="button"
              >
                Cancelar
              </button>
            </div>
            <PinInput value={pin} onChange={setPin} length={4} />
            <div className="mt-6 w-full">
              <NumericKeypad value={pin} length={4} onChange={setPin} />
            </div>
            <button
              className={`w-full mt-8 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2
                ${pin.length === 4
                  ? 'bg-gray-900 text-white hover:bg-black shadow-sm'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
              disabled={pin.length !== 4}
              onClick={handleLogin}
            >
              Ingresar
            </button>
            {error && (
              <div className="w-full mt-4 text-center text-red-600 text-sm">
                {error}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Botón para volver al inicio (Landing Page) */}
      <div className="absolute top-4 left-4">
        <button
          onClick={() => window.location.href = '/'}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors bg-white/80 backdrop-blur-sm px-3 py-2 rounded-lg border border-gray-200 shadow-sm"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m12 19-7-7 7-7" />
            <path d="M19 12H5" />
          </svg>
          Volver al Inicio
        </button>
      </div>
    </div>
  );
} 